import { useState } from "react";
import api from "@/lib/api";
import { ResumePreview } from "@/components/ResumePreview";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Upload, Loader2, FileText, Wand2, Printer, Check, Sparkles, ArrowRight, ShieldCheck,
} from "lucide-react";

const TEMPLATES = ["modern", "classic", "minimal"];

const PLANS = [
  { key: "normal", label: "Normal", price: 99, original: 400, blurb: "AI-rewritten, ATS-friendly resume you can edit & download.", perks: ["AI rewrite", "Editable result", "Print / PDF"] },
  { key: "professional", label: "Professional", price: 399, original: 3000, blurb: "Premium rewrite with deeper polish for senior & specialist roles.", perks: ["Everything in Normal", "Priority phrasing", "Executive tone"] },
];

const ImproveResume = () => {
  const [step, setStep] = useState("upload"); // upload | plan | pay | edit
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(null); // {url, filename, text}
  const [pasteText, setPasteText] = useState("");
  const [contact, setContact] = useState({ name: "", email: "" });
  const [plan, setPlan] = useState(null);
  const [order, setOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paid, setPaid] = useState(false);
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState("modern");
  const [aiLoading, setAiLoading] = useState("");

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/improve/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(res.data);
      if (!res.data.text_len) toast.warning("Couldn't read much text — you can still improve it.");
      else toast.success("Resume uploaded & read");
      setStep("plan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const choosePlan = async (p) => {
    setPlan(p);
    setProcessing(true);
    try {
      const res = await api.post("/improve/orders", {
        tier: p.key, resume_url: uploaded.url, resume_text: uploaded.text || "",
        name: contact.name, email: contact.email,
      });
      setOrder(res.data);
      const gen = await api.post(`/improve/orders/${res.data.id}/generate`);
      setData({ fullName: "", jobTitle: "", email: "", phone: "", location: "", website: "", summary: "", skills: "", experience: [], education: [], ...gen.data.data });
      setStep("edit");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create preview"); }
    finally { setProcessing(false); }
  };

  const unlockAndDownload = async () => {
    if (paid) { window.print(); return; }
    setProcessing(true);
    try {
      await api.post(`/improve/orders/${order.id}/pay`);
      setPaid(true);
      toast.success("Payment successful (demo) — watermark removed");
      setTimeout(() => window.print(), 400);
    } catch (e) {
      toast.error("Payment failed");
    } finally { setProcessing(false); }
  };

  const update = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const saveData = async (next) => {
    try { await api.put(`/improve/orders/${order.id}/data`, { order_id: order.id, data: next }); } catch (e) {}
  };
  const aiHelp = async (type) => {
    setAiLoading(type);
    try {
      const res = await api.post("/improve/ai", { order_id: order.id, type, context: { jobTitle: data.jobTitle, experience: data.experience, skills: data.skills, summary: data.summary } });
      const val = res.data.result?.trim();
      const next = { ...data, [type === "skills" ? "skills" : "summary"]: val };
      setData(next); saveData(next);
      toast.success("AI updated");
    } catch (e) { toast.error("AI failed"); }
    finally { setAiLoading(""); }
  };

  const disc = (o, p) => Math.round(((o - p) / o) * 100);

  return (
    <div className="font-body min-h-[calc(100vh-4rem)]" data-testid="improve-page">
      {/* Step indicator */}
      {step !== "edit" && (
        <section className="max-w-5xl mx-auto px-5 pt-14 pb-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium mb-5">
            <Sparkles className="h-3.5 w-3.5 text-accent" /> Upload & Improve — 50% introductory offer
          </div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight">Turn your old resume into a winning one</h1>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">Upload your existing resume, pay a small fee, and our AI rewrites it into a polished, ATS-ready resume you can edit and download.</p>
        </section>
      )}

      {/* Upload */}
      {step === "upload" && (
        <section className="max-w-md mx-auto px-5 pb-20">
          <Card className="p-8 text-center" data-testid="improve-upload-card">
            <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><FileText className="h-7 w-7 text-primary" /></div>
            <h3 className="font-heading font-semibold text-xl">Upload your resume</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-5">PDF, DOC or DOCX — up to 8MB</p>
            <label className="inline-flex items-center gap-2 text-sm px-5 py-3 rounded-full bg-primary text-primary-foreground cursor-pointer hover:opacity-90 transition-opacity font-semibold" data-testid="improve-upload-label">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Reading…" : "Choose file"}
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={upload} data-testid="improve-upload-input" />
            </label>

            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or paste your resume</span><div className="h-px flex-1 bg-border" />
            </div>
            <Textarea
              rows={6}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Paste your existing resume text here…"
              className="text-left"
              data-testid="improve-paste-input"
            />
            <Button
              variant="outline"
              className="rounded-full w-full mt-3 font-semibold"
              disabled={pasteText.trim().length < 30}
              onClick={() => {
                setUploaded({ url: "", filename: "Pasted resume", text: pasteText.trim(), text_len: pasteText.trim().length });
                toast.success("Resume text added");
                setStep("plan");
              }}
              data-testid="improve-paste-btn"
            >
              Use pasted text
            </Button>
          </Card>
        </section>
      )}

      {/* Plans */}
      {step === "plan" && (
        <section className="max-w-4xl mx-auto px-5 pb-20">
          <div className="flex items-center justify-center gap-2 text-sm text-green-600 mb-6"><Check className="h-4 w-4" /> {uploaded?.filename} uploaded</div>
          <div className="grid sm:grid-cols-2 gap-6">
            {PLANS.map((p) => (
              <Card key={p.key} className={`p-7 relative overflow-hidden ${p.key === "professional" ? "border-primary shadow-lg" : ""}`} data-testid={`plan-${p.key}`}>
                <div className="absolute top-4 right-4 bg-accent text-accent-foreground text-[10px] font-bold px-2 py-1 rounded-full">{disc(p.original, p.price)}% OFF</div>
                <h3 className="font-heading font-semibold text-xl">{p.label}</h3>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-heading font-bold text-3xl">₹{p.price}</span>
                  <span className="text-muted-foreground line-through text-lg">₹{p.original}</span>
                </div>
                <p className="text-xs text-accent font-semibold mt-0.5">Introductory offer — 50% off</p>
                <p className="text-sm text-muted-foreground mt-3">{p.blurb}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.perks.map((x) => <li key={x} className="text-sm flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> {x}</li>)}
                </ul>
                <Button onClick={() => choosePlan(p)} disabled={processing} className="rounded-full w-full mt-6 font-semibold" data-testid={`choose-${p.key}`}>
                  {processing ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating preview…</> : <>See free preview <ArrowRight className="ml-1.5 h-4 w-4" /></>}
                </Button>
              </Card>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-5 flex items-center justify-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Preview is free — you only pay to remove the watermark & download.</p>
        </section>
      )}

      {/* Edit */}
      {step === "edit" && data && (
        <div className="min-h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] flex flex-col">
          <div className="border-b border-border bg-card px-5 py-3 flex items-center justify-between gap-3 flex-wrap print:hidden">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              <span className="font-heading font-semibold">Your improved resume is ready — edit & download</span>
              {!paid && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-accent bg-accent/10 rounded-full px-2 py-0.5" data-testid="imp-preview-badge">
                  Free preview · watermarked
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-full border border-border overflow-hidden">
                {TEMPLATES.map((t) => (
                  <button key={t} onClick={() => setTemplate(t)} data-testid={`imp-template-${t}`}
                    className={`text-xs px-3 py-1.5 capitalize ${template === t ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>{t}</button>
                ))}
              </div>
              {paid ? (
                <Button onClick={() => window.print()} className="rounded-full" data-testid="imp-print"><Printer className="h-4 w-4 mr-1.5" /> Download clean PDF</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => window.print()} className="rounded-full" data-testid="imp-download-free">
                    <Printer className="h-4 w-4 mr-1.5" /> Free (watermarked)
                  </Button>
                  <Button onClick={unlockAndDownload} disabled={processing} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="imp-unlock">
                    {processing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />} Remove watermark — ₹{order?.amount}
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="flex-1 grid lg:grid-cols-2 lg:overflow-hidden">
            <div className="lg:overflow-y-auto p-6 space-y-4 print:hidden">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Full name</Label><Input value={data.fullName} onChange={(e) => update("fullName", e.target.value)} data-testid="imp-name" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Job title</Label><Input value={data.jobTitle} onChange={(e) => update("jobTitle", e.target.value)} data-testid="imp-title" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={data.email} onChange={(e) => update("email", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={data.phone} onChange={(e) => update("phone", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Location</Label><Input value={data.location} onChange={(e) => update("location", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Website</Label><Input value={data.website} onChange={(e) => update("website", e.target.value)} /></div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between"><Label className="text-xs">Summary</Label>
                  <Button size="sm" variant="outline" className="rounded-full h-7 text-primary border-primary/40" onClick={() => aiHelp("summary")} disabled={aiLoading === "summary"} data-testid="imp-ai-summary">
                    {aiLoading === "summary" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />} AI
                  </Button>
                </div>
                <Textarea rows={4} value={data.summary} onChange={(e) => update("summary", e.target.value)} data-testid="imp-summary" />
              </div>
              {(data.experience || []).map((exp, i) => (
                <Card key={i} className="p-4 space-y-2">
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Input placeholder="Role" value={exp.role || ""} onChange={(e) => { const a = [...data.experience]; a[i] = { ...a[i], role: e.target.value }; update("experience", a); }} />
                    <Input placeholder="Company" value={exp.company || ""} onChange={(e) => { const a = [...data.experience]; a[i] = { ...a[i], company: e.target.value }; update("experience", a); }} />
                  </div>
                  <Textarea rows={3} placeholder="Bullets" value={exp.bullets || ""} onChange={(e) => { const a = [...data.experience]; a[i] = { ...a[i], bullets: e.target.value }; update("experience", a); }} />
                </Card>
              ))}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between"><Label className="text-xs">Skills</Label>
                  <Button size="sm" variant="outline" className="rounded-full h-7 text-primary border-primary/40" onClick={() => aiHelp("skills")} disabled={aiLoading === "skills"} data-testid="imp-ai-skills">
                    {aiLoading === "skills" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />} AI
                  </Button>
                </div>
                <Input value={data.skills} onChange={(e) => update("skills", e.target.value)} data-testid="imp-skills" />
              </div>
              <Button variant="outline" className="rounded-full w-full" onClick={() => saveData(data)} data-testid="imp-save">Save changes</Button>
            </div>
            <div className="lg:overflow-y-auto workspace-bg p-6 lg:p-10 flex justify-center print:p-0 print:bg-white">
              <div className="relative w-full max-w-[520px] print:max-w-none" id="resume-print-area">
                <ResumePreview data={data} template={template} />
                {!paid && (
                  <div className="resume-watermark" aria-hidden="true" data-testid="imp-watermark">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className="wm-row">ResumePro24 • PREVIEW&nbsp;&nbsp;•&nbsp;&nbsp;ResumePro24 • PREVIEW</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImproveResume;
